import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorInfo: null,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        this.setState({
            error,
            errorInfo,
        });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="min-h-screen bg-[#f8f1e9] flex items-center justify-center p-4">
                    <div className="max-w-2xl w-full">
                        {/* Decorative background */}
                        <div className="absolute inset-0 overflow-hidden pointer-events-none">
                            <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-coral-200 rounded-full blur-3xl opacity-20" />
                            <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-teal-200 rounded-full blur-3xl opacity-20" />
                        </div>

                        <div className="relative bg-white rounded-3xl shadow-2xl p-8 md:p-12 border-2 border-neutral-200">
                            {/* Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="w-20 h-20 rounded-full bg-coral-100 flex items-center justify-center">
                                    <AlertTriangle className="w-10 h-10 text-coral-600" />
                                </div>
                            </div>

                            {/* Content */}
                            <div className="text-center space-y-4 mb-8">
                                <h1 className="text-3xl md:text-4xl font-crayon text-neutral-900">
                                    Oops! Something Went Wrong
                                </h1>
                                <p className="text-lg text-neutral-600 font-story">
                                    Don't worry, even the best storytellers sometimes lose their place. Let's try to get back on track.
                                </p>
                            </div>

                            {/* Error Details (only in development) */}
                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <div className="mb-8 p-4 bg-neutral-100 rounded-xl border border-neutral-200 overflow-auto max-h-64">
                                    <p className="text-sm font-mono text-neutral-700 mb-2 font-bold">
                                        {this.state.error.toString()}
                                    </p>
                                    {this.state.errorInfo && (
                                        <pre className="text-xs font-mono text-neutral-600 whitespace-pre-wrap">
                                            {this.state.errorInfo.componentStack}
                                        </pre>
                                    )}
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button
                                    onClick={this.handleReset}
                                    variant="crayon"
                                    size="lg"
                                    className="gap-2 shadow-xl hover:scale-105 transition-transform"
                                >
                                    <RefreshCw className="w-5 h-5" />
                                    Try Again
                                </Button>
                                <Button
                                    onClick={() => window.location.href = '/'}
                                    variant="outline"
                                    size="lg"
                                    className="gap-2"
                                >
                                    <Home className="w-5 h-5" />
                                    Go Home
                                </Button>
                            </div>

                            {/* Support Message */}
                            <div className="mt-8 pt-6 border-t border-neutral-200">
                                <p className="text-sm text-neutral-500 text-center">
                                    If this problem persists, please refresh the page or contact support.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
